import { useEffect, useState } from "react";
import axios from "axios";
import { Shield, Phone, Mail, RefreshCw, Inbox } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadsDashboard() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([axios.get(`${API}/leads`), axios.get(`${API}/leads/stats`)]);
      setLeads(l.data);
      setStats(s.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-5 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="grid place-items-center h-9 w-9 rounded-lg bg-red-700"><Shield className="h-5 w-5" /></div>
            <div>
              <h1 className="font-display font-bold text-lg leading-tight">Templar Armor — Leads</h1>
              <p className="text-xs text-slate-400">Quote requests dashboard</p>
            </div>
          </div>
          <button onClick={load} data-testid="refresh-leads-btn" className="flex items-center gap-2 text-sm font-semibold bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 transition-colors">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        <div className="grid grid-cols-2 gap-4 mb-8 max-w-sm">
          <div className="rounded-xl bg-white border border-slate-200 p-5">
            <div className="font-display font-extrabold text-3xl text-slate-900" data-testid="stat-total">{stats.total}</div>
            <div className="text-sm text-slate-500 font-semibold">Total Leads</div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-5">
            <div className="font-display font-extrabold text-3xl text-red-700" data-testid="stat-new">{stats.new}</div>
            <div className="text-sm text-slate-500 font-semibold">New</div>
          </div>
        </div>

        {leads.length === 0 && !loading ? (
          <div className="text-center py-20 text-slate-400">
            <Inbox className="h-12 w-12 mx-auto mb-3" />
            <p className="font-semibold">No leads yet. Quote requests will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="leads-list">
            {leads.map((lead) => (
              <div key={lead.id} data-testid="lead-row" className="rounded-xl bg-white border border-slate-200 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-display font-bold text-lg text-slate-900">{lead.name}</h3>
                      <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{lead.status}</Badge>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                      <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 hover:text-red-700"><Phone className="h-3.5 w-3.5" /> {lead.phone}</a>
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 hover:text-red-700"><Mail className="h-3.5 w-3.5" /> {lead.email}</a>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400">{new Date(lead.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  {lead.service && <span className="bg-slate-100 rounded-full px-3 py-1 font-semibold text-slate-700">Service: {lead.service}</span>}
                  {lead.property_type && <span className="bg-slate-100 rounded-full px-3 py-1 font-semibold text-slate-700">{lead.property_type}</span>}
                  {lead.timeline && <span className="bg-slate-100 rounded-full px-3 py-1 font-semibold text-slate-700">{lead.timeline}</span>}
                  {lead.address && <span className="bg-slate-100 rounded-full px-3 py-1 font-semibold text-slate-700">{lead.address}</span>}
                  {Object.entries(lead.answers || {}).map(([k, v]) => (
                    <span key={k} className="bg-red-50 text-red-800 rounded-full px-3 py-1 font-semibold">{k}: {v}</span>
                  ))}
                </div>
                {lead.details && <p className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{lead.details}</p>}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
